/**
 * CopaRegulamento — Regulamento/explicação da campanha "O Álbum BB 2026".
 *
 * Conteúdo convertido do documento oficial (Definitivo Campanha album BB 2026.docx).
 * Aberto a partir do "Ver regras da campanha" no onboarding (CopaOnboardingModal).
 *
 * Renderizado como portal react-modal, full-screen e rolável, no tema da campanha.
 */

import React from 'react';
import Modal from 'react-modal';
import { registerInteractionCampaign } from 'services/actionlog.service';
import styles from './CopaRegulamento.module.scss';

/**
 * PDF oficial do regulamento (S3) — estático. O texto temático abaixo é o
 * explicador amigável; o PDF é a versão oficial/legal. Trocar o conteúdo do
 * PDF = sobrescrever o arquivo no S3 (mesma URL); trocar a URL exige redeploy.
 */
const REGULAMENTO_PDF_URL =
  'https://s3.amazonaws.com/funifier/games/635ae06f8cf58e411eeb0b8e/images/6a3dab230f62455d58f1fee6_original_Campanha%20album%20BB%202026.pdf';

interface CopaRegulamentoProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CopaRegulamento({ isOpen, onClose }: CopaRegulamentoProps) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      overlayClassName={styles.overlay}
      className={styles.modal}
      portalClassName="relative z-[1200]"
      contentLabel="Regras da campanha Álbum BB 2026"
    >
      {/* Cabeçalho fixo */}
      <div className={styles.header}>
        <div className={styles.headerStar} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="#fbfd00">
            <path d="M12 2l2.4 6.9H22l-6 4.4 2.3 7-6.3-4.6L5.7 20.3 8 13.3l-6-4.4h7.6z" />
          </svg>
        </div>
        <div className={styles.headerContent}>
          <div className={styles.pill}>Regulamento · 1S2026</div>
          <h1 className={styles.title}>Seleção Joga Junto — O Álbum BB 2026</h1>
          <p className={styles.subtitle}>
            Reconheça. Ganhe figurinhas. Complete o álbum.
          </p>

          <a
            className={styles.pdfBtn}
            href={REGULAMENTO_PDF_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              registerInteractionCampaign('me', 'copa_interaction', { type: 'download_rules_pdf' })
            }
            aria-label="Abrir o regulamento oficial em PDF em nova aba"
          >
            <span aria-hidden="true">📄</span> Baixar regulamento oficial (PDF)
          </a>
        </div>
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Fechar regras">
          ✕
        </button>
      </div>

      {/* Corpo rolável */}
      <div className={styles.body}>
        <section className={styles.section} aria-label="A campanha em uma olhada">
          <h2 className={styles.h2}>A campanha em uma olhada</h2>
          <div className={styles.cards}>
            <div className={styles.card}>
              <div className={styles.cardLabel}><span aria-hidden="true">🎯</span> O que é</div>
              <p>Um álbum de figurinhas digital, no estilo dos álbuns da Copa — só que as figurinhas são os colegas de trabalho.</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}><span aria-hidden="true">📱</span> Onde acontece</div>
              <p>Dentro do site Joga Junto BB.</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}><span aria-hidden="true">📅</span> Quando</div>
              <p>De 26 de junho a 20 de julho de 2026 (25 dias).</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}><span aria-hidden="true">🏅</span> O que você ganha</div>
              <p>Figurinhas, o orgulho de completar a Seleção BB e até <b>200 pontos</b> na ação de engajamento (Pontos Joga Junto).</p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>A grande ideia</h2>
          <p>
            Durante a Copa do Mundo, o país inteiro se junta para torcer. Vamos aproveitar essa energia
            para celebrar quem faz o BB acontecer: cerca de 23 mil funcionários que participam da Seleção
            Joga Junto. Eles já são, na prática, os convocados do Banco do Brasil — um time selecionado que
            agora ganha o seu próprio álbum. Ao abrir o primeiro pacote, cada um é escalado para entrar em campo.
          </p>
          <p>
            A ideia é simples e emocionante: cada colega é uma figurinha. Do mais novo da casa ao mais
            experiente, cada participante pode ser colecionado. Não são craques da TV, são os craques de
            verdade — os que fazem o programa acontecer todos os dias.
          </p>
          <p className={styles.quote}><span aria-hidden="true">💙💛</span> "A figurinha mais valiosa é a de quem joga junto com você."</p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Como funciona, na prática</h2>
          <p>A campanha gira em torno de um gesto que já existe no Joga Junto BB: <b>reconhecer colegas</b>.</p>
          <ul className={styles.list}>
            <li><span aria-hidden="true">🎁</span> Abra seu <b>Pacote do Dia</b> → 1 figurinha por dia (em geral, um Titular).</li>
            <li><span aria-hidden="true">✨</span> Reconheceu um colega ontem? → <b>+1 figurinha hoje</b>.</li>
            <li><span aria-hidden="true">📈</span> Cole no álbum → monte o time completo.</li>
          </ul>
          <p className={styles.note}>
            <b>Titulares</b> = colegas que TE reconhecem · <b>Craques</b> (épicas) = colegas que VOCÊ reconhece.
            ↻ Todo dia, o seu time cresce.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Como você ganha figurinhas</h2>
          <ol className={styles.steps}>
            <li>
              <b>O Pacote do Dia (sua base).</b> Uma vez por dia, você abre um pacote com 1 figurinha —
              em geral um Titular: o rosto de um colega que te reconheceu.
            </li>
            <li>
              <b>Reconhecer rende mais 1 pacote.</b> Se você enviou 1 reconhecimento no dia anterior, no dia
              seguinte recebe +1 pacote. Enviou 2 reconhecimentos? Recebe +2 pacotes, e assim por diante.
              O bônus é recebido uma vez por dia.
            </li>
            <li>
              <b>As cartas épicas: os Craques.</b> As figurinhas mais valiosas trazem o rosto dos colegas
              que você reconhece. São épicas e saem só de vez em quando — completar a prateleira de Craques
              é o grande desafio.
            </li>
            <li>
              <b>Você monta a sua seleção.</b> Tudo o que você coleta, inclusive as repetidas, fica guardado
              no seu repositório, separado por tipo (Titulares, Craques, Lendárias). É você quem escolhe
              quais colegas entram na escalação e quando.
            </li>
          </ol>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Um dia na ação — a história da Ana</h2>
          <p>Para deixar bem concreto, acompanhe um dia da Ana, funcionária em um dos escritórios do BB.</p>
          <ul className={styles.list}>
            <li><span aria-hidden="true">🌅</span> <b>De manhã,</b> a Ana abre o app: "Seu Pacote do Dia chegou!". Como reconheceu um colega ontem, recebeu +1 pacote de bônus. Uma carta é Titular — uma colega que reconheceu a Ana. A outra, que sorte, é uma épica de Craque: um colega que a Ana reconheceu lá atrás. <span aria-hidden="true">🎉</span></li>
            <li><span aria-hidden="true">☕</span> <b>No meio da manhã,</b> um colega ajuda a destravar uma demanda. A Ana toca em Reconhecer, escolhe o colega e envia um reconhecimento de colaboração. Amanhã ela receberá +1 pacote.</li>
            <li><span aria-hidden="true">📈</span> <b>No fim do dia,</b> a Ana abre o repositório e escolhe a dedo quais colegas colar na escalação.</li>
            <li><span aria-hidden="true">🏆</span> <b>Semanas depois,</b> completa a escalação inteira (e ganha a carta de Técnico), reúne os Craques épicos, conquista 2 Lendárias e fica a um passo da cobiçada Taça.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>O álbum: o que dá para colecionar</h2>
          <p>
            São <b>12 figurinhas</b> para montar a sua seleção: 7 Titulares, 4 Craques e 1 Técnico, além de
            <b> 3 cartas Lendárias</b> especiais. O time tem a sua cara — um elenco que você monta, comanda e leva ao título.
          </p>
          <ul className={styles.list}>
            <li><span aria-hidden="true">⚽</span> <b>Escalação Titular</b> → 7 cartas — colegas que TE reconhecem (comuns).</li>
            <li><span aria-hidden="true">🌟</span> <b>Craques (épicas)</b> → 4 cartas — colegas que VOCÊ reconhece (épicas).</li>
            <li><span aria-hidden="true">🎽</span> <b>Técnico</b> → 1 carta — ao completar a escalação (marco).</li>
            <li><span aria-hidden="true">🏆</span> <b>Lendárias</b> → 3 cartas — só por desafio (topo).</li>
          </ul>
          <p className={styles.note}>
            <span aria-hidden="true">🎲</span> Cada figurinha é sorteada da sua lista real de reconhecimentos do ciclo: os que você recebeu
            viram Titulares e os que você enviou viram Craques épicos. Cada reconhecimento é uma entrada e
            sai uma única vez — se você reconheceu o João 3 vezes, ele pode sair até 3 vezes (a 1ª você cola,
            as outras viram repetidas).
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}><span aria-hidden="true">⚽</span> Escalação Titular (7 cartas) — 50 pontos</h2>
          <p>Os colegas que te reconheceram, vindos do Pacote do Dia. Cada posição é temática de um comportamento:</p>
          <table className={styles.table}>
            <thead>
              <tr><th>Posição</th><th>Qtd.</th><th>Comportamento</th></tr>
            </thead>
            <tbody>
              <tr><td><span aria-hidden="true">🧤</span> Goleiro</td><td>1</td><td>Foco no cliente</td></tr>
              <tr><td><span aria-hidden="true">🛡️</span> Defensores</td><td>2</td><td>Colaboração</td></tr>
              <tr><td><span aria-hidden="true">🎯</span> Meio-campo</td><td>2</td><td>Inovação</td></tr>
              <tr><td><span aria-hidden="true">⚡</span> Atacantes</td><td>2</td><td>Protagonismo</td></tr>
            </tbody>
          </table>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}><span aria-hidden="true">🌟</span> Craques (4) + <span aria-hidden="true">🎽</span> Técnico (1) — 50 pontos</h2>
          <p>
            As cartas mais valiosas e épicas: os rostos dos colegas que <b>você</b> reconhece. A carta não cai
            a cada reconhecimento — é sorteada de vez em quando. Reunir os 4 Craques é o grande desafio.
          </p>
          <p className={styles.note}>
            <span aria-hidden="true">🎽</span> <b>Técnico:</b> a carta de um colega que você reconheceu com o comportamento <b>Liderança Integradora</b>.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}><span aria-hidden="true">🏆</span> Lendárias — 3 cartas especiais — 100 pontos</h2>
          <p>As três cartas lendárias não saem em pacote nenhum. Só são conquistadas com ações ao longo da campanha:</p>
          <table className={styles.table}>
            <thead>
              <tr><th>Carta</th><th>Como conquistar</th><th>Dificuldade</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><span aria-hidden="true">🐻</span> Mascote Joga Junto</td>
                <td>Ter reconhecido ou reconhecer colegas em todos os 5 tipos de comportamento diferentes durante o semestre.</td>
                <td>Acessível para quem participa</td>
              </tr>
              <tr>
                <td><span aria-hidden="true">🏟️</span> Estádio BB</td>
                <td>Abrir o Pacote do Dia em pelo menos 8 dias diferentes.</td>
                <td>Média</td>
              </tr>
              <tr>
                <td><span aria-hidden="true">🏆</span> A Taça</td>
                <td>Completar a Escalação Titular e ter as duas lendárias acima.</td>
                <td>Para os campeões</td>
              </tr>
            </tbody>
          </table>
          <p className={styles.note}>
            <span aria-hidden="true">😍</span> Todas as figurinhas são colegas reais: os Titulares são as pessoas que reconhecem você; os
            Craques são as que você reconhece. Por isso seus colegas são as figurinhas mais valiosas do mundo.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>O que o funcionário ganha</h2>
          <p>Até <b>200 pontos</b>, acumulados em Pontos Joga Junto:</p>
          <ul className={styles.list}>
            <li>50 pontos — completar a 1ª seção (Titulares, 7)</li>
            <li>50 pontos — completar a 2ª seção (Craques + Técnico, 5)</li>
            <li>100 pontos — completar a 3ª seção (Lendárias, 3)</li>
          </ul>
          <p className={styles.note}><b>Total: 200 pontos.</b></p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Por que vai funcionar</h2>
          <ul className={styles.list}>
            <li><span aria-hidden="true">🎁</span> <b>A surpresa diária.</b> Ninguém sabe quais figurinhas vêm no pacote.</li>
            <li><span aria-hidden="true">💚</span> <b>É sobre gente de verdade.</b> Ver o rosto de um colega virar figurinha cria uma conexão que nenhum jogo genérico tem.</li>
            <li><span aria-hidden="true">⏳</span> <b>Tem hora para acabar.</b> O prazo de 20 de julho cria aquele empurrãozinho saudável para não deixar para depois.</li>
            <li><span aria-hidden="true">🤝</span> <b>Reconhecer monta o time.</b> Cada colega que você reconhece pode virar um Craque épico; cada um que reconhece você pode virar um Titular.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Perguntas frequentes</h2>
          <dl className={styles.faq}>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>Preciso baixar ou instalar algo novo?</dt>
              <dd className={styles.faqA}>Não. Tudo acontece dentro do site do Joga Junto BB.</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>Todo mundo consegue completar o álbum inteiro?</dt>
              <dd className={styles.faqA}>Talvez. O seu álbum reflete o seu reconhecimento de verdade: os Titulares são os colegas que reconheceram você, e os Craques, os que você reconhece. Quem está no fluxo do reconhecimento monta o time titular com folga; completar os Craques épicos e as Lendárias é o desafio dos mais dedicados.</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>E quando eu completo o álbum inteiro?</dt>
              <dd className={styles.faqA}>Os pacotes diários se encerram — ninguém compra figurinha depois de fechar o álbum. Agora é só aguardar os pontos Joga Junto. Missão cumprida!</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>Posso trocar ou tirar uma figurinha que já colei?</dt>
              <dd className={styles.faqA}>Sim. Você poderá substituir a figurinha por outra do seu repositório.</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>As figurinhas são fotos de pessoas reais? Tem questão de privacidade?</dt>
              <dd className={styles.faqA}>As figurinhas usam as fotos ou avatares que os próprios funcionários já têm na plataforma. Não criamos nenhum cadastro novo de foto.</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>Como funcionam os pontos?</dt>
              <dd className={styles.faqA}>A pontuação entra como Pontos Joga Junto (o mesmo mecanismo das ações de engajamento).</dd>
            </div>
            <div className={styles.faqItem}>
              <dt className={styles.faqQ}>Qual o período da campanha?</dt>
              <dd className={styles.faqA}>De 26 de junho a 20 de julho de 2026.</dd>
            </div>
          </dl>
        </section>

        <p className={styles.footer}>
          <span aria-hidden="true">⚽</span> Seleção Joga Junto — O Álbum BB 2026 · Porque a figurinha mais valiosa é a de quem joga junto com você.
        </p>

        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={onClose} type="button">
            Entendi, voltar
          </button>
        </div>
      </div>
    </Modal>
  );
}
